// What people do on benches and while standing: reading, phoning, wearing sunglasses.
import * as THREE from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { PI } from '../../lib/math.js';
import { rnd, rr } from '../../lib/random.js';
import { scene } from '../../core/scene.js';
import { Ph } from '../../lib/factory.js';
import { V } from '../../world/sagrada.js';
import { sitSpots, standSpots } from '../crowd.js';
import { state } from '../../core/state.js';
import { idlers } from './loader.js';
import { makeClip, paddleFrame } from './animation.js';
import { prepared } from './prepared.js';
import { build, crowd, dress, fatten, outfit, swimOutfit } from './outfits.js';
import { HQ, X, Z, rotW, seat } from './seating.js';
import { helmetGeo } from './rider.js';
import { holdProp, lensM, makeBook, makePhone } from './props.js';

export let lyingActivity, addGlasses;

export function placeSitters() {
  // lying on a towel: hold a book or phone above the face (on the back) or have a book open in front (face down)
  lyingActivity = function lyingActivity(holder, model, b, kind) {
    holder.updateMatrixWorld(true);
    const u = 1;
    const head = holder.worldToLocal(b('Head').getWorldPosition(new THREE.Vector3())),
      chest = holder.worldToLocal(b('Chest').getWorldPosition(new THREE.Vector3()));
    if (kind === 'bookOnTowel') {
      // open on the towel just beyond the head, in the direction the body points
      const dir = head.clone().sub(chest).setY(0).normalize(),
        bk = makeBook(u);
      bk.position.copy(head).addScaledVector(dir, 0.17).setY(0.012);
      bk.rotation.set(-PI / 2, 0, Math.atan2(dir.x, dir.z));
      holder.add(bk);
      return;
    }
    const book = kind === 'bookUp',
      C = head
        .clone()
        .lerp(chest, 0.35)
        .add(V(0, book ? 0.2 : 0.17, 0)),
      dx = book ? 0.055 : 0.022;
    const side = V(1, 0, 0); // the model's left is +x of the holder
    const backW = chest.clone().sub(C).normalize().applyQuaternion(HQ); // from the prop toward the chest, in world
    holdProp(
      model,
      b,
      holder.localToWorld(C.clone()),
      side.clone().applyQuaternion(HQ),
      backW,
      dx,
      ['L', 'R'],
      (sg) => V(sg, -1, 0).applyQuaternion(HQ),
    );
    const prop = book ? makeBook(u) : makePhone(u);
    prop.position.copy(C);
    // face down toward the reader, the top of the page toward the head
    const toHead = head.clone().sub(chest).setY(0).normalize();
    prop.rotation.set(PI / 2, 0, 0);
    prop.rotateOnWorldAxis(V(0, 1, 0), Math.atan2(toHead.x, toHead.z));
    holder.add(prop);
  };
  // sunglasses fitted while the model still stands upright facing +z, then carried by the head bone
  addGlasses = function addGlasses(model, b) {
    model.updateMatrixWorld(true);
    // size and place them from the actual head: the head bone sits at the base of the skull; eyes are ~45% of the
    // head height up and the face is ~half the head width forward (models differ, so measure the skinned mesh)
    const head = b('Head'),
      p = head.getWorldPosition(new THREE.Vector3()),
      fwd = model.getWorldDirection(new THREE.Vector3()),
      up = V(0, 1, 0);
    const box = new THREE.Box3();
    let mesh;
    model.traverse((o) => {
      if (o.isSkinnedMesh) mesh = o;
    });
    const v = new THREE.Vector3(),
      pos = mesh.geometry.attributes.position;
    for (let i = 0; i < pos.count; i += 3) {
      mesh.getVertexPosition(i, v);
      mesh.localToWorld(v);
      if (v.y > p.y - 0.01) box.expandByPoint(v);
    }
    const hs = box.getSize(new THREE.Vector3()),
      u = Math.max(hs.x, hs.z) / 0.17;
    const eye = p
        .clone()
        .addScaledVector(up, hs.y * 0.42)
        .addScaledVector(fwd, Math.max(hs.x, hs.z) * 0.5 + 0.004),
      g = new THREE.Group();
    for (const sx of [-1, 1]) {
      const l = new THREE.Mesh(new THREE.CircleGeometry(0.0135 * u, 16).scale(1.15, 1, 1), lensM);
      l.position.x = sx * 0.021 * u;
      g.add(l);
    }
    g.add(
      Object.assign(
        new THREE.Mesh(new THREE.BoxGeometry(0.016 * u, 0.004 * u, 0.004 * u), lensM),
        {},
      ),
    );
    for (const sx of [-1, 1]) {
      const t = new THREE.Mesh(new THREE.BoxGeometry(0.003 * u, 0.003 * u, 0.05 * u), lensM);
      t.position.set(sx * 0.037 * u, 0, -0.025 * u);
      g.add(t);
    }
    g.position.copy(eye);
    g.lookAt(eye.clone().add(fwd));
    scene.add(g);
    g.updateMatrixWorld(true);
    head.attach(g);
  };
  // sitting on a bench (the model faces +z of its holder, hip at `hip`)
  function benchActivity(holder, model, b, hip, kind) {
    const u = 1,
      W = (v) => holder.localToWorld(v.clone());
    const pole = (sg) => V(sg, -0.8, -0.3).applyQuaternion(HQ);
    if (kind === 'read' || kind === 'phone') {
      const read = kind === 'read',
        C = hip.clone().add(V(0, (read ? 0.2 : 0.17) * u, (read ? 0.19 : 0.16) * u)),
        dx = (read ? 0.055 : 0.022) * u;
      rotW(b('Head'), X, read ? 0.45 : 0.55);
      holdProp(
        model,
        b,
        W(C),
        V(1, 0, 0).applyQuaternion(HQ),
        V(0, -0.35, -1).normalize().applyQuaternion(HQ),
        dx,
        ['L', 'R'],
        pole,
      );
      const prop = read ? makeBook(u) : makePhone(u);
      prop.position.copy(C);
      prop.rotation.x = read ? 0.75 : 0.85;
      holder.add(prop);
    } else {
      // a call: the right hand brings the phone to the ear, the left stays on the lap
      holder.updateMatrixWorld(true);
      const head = holder.worldToLocal(b('Head').getWorldPosition(new THREE.Vector3()));
      const ear = head.clone().add(V(-0.06 * u, 0.05 * u, 0.01 * u));
      // the phone is held against the ear from below: fingers up its back, the wrist under the jaw
      holdProp(
        model,
        b,
        W(ear),
        V(0, 0, 1).applyQuaternion(HQ),
        V(0, -1, 0).applyQuaternion(HQ),
        0.02,
        ['R'],
        (sg) => V(sg, -1, 0.2).applyQuaternion(HQ),
      );
      rotW(b('Head'), Z, -0.12);
      const ph = makePhone(u);
      ph.position.copy(ear);
      ph.rotation.set(0, -PI / 2, 0.25);
      holder.add(ph);
    }
  }
  // sitters: hips forward enough that the knees clear the seat's front edge (it spans ±.12)
  sitSpots.forEach((sp, i) => {
    const holder = new THREE.Group();
    holder.position.set(sp.x, 0, sp.z);
    holder.rotation.y = sp.ry;
    scene.add(holder);
    const P = crowd[(i * 5 + 1) % crowd.length],
      hip = V(0, sp.y + 0.05, 0.02),
      { model, b } = seat(P, holder, P.k * rr(0.94, 1.04), hip, 'bench');
    // something to do: read a book, look at the phone, or talk on it (the rest just sit and watch the world go by)
    const r = rnd();
    if (r < 0.3) benchActivity(holder, model, b, hip, 'read');
    else if (r < 0.55) benchActivity(holder, model, b, hip, 'phone');
    else if (r < 0.75) benchActivity(holder, model, b, hip, 'call');
    holder.updateMatrixWorld(true);
    holder.matrixWorldAutoUpdate = false; // a still pose: its matrices never change
    sp.holder = holder; // Polyfork people may take the seat later
  });
  if (state.jetSki) {
    // the rider wears an orange life vest over their clothes, hands on the grips, leaning forward
    const P = prepared[0],
      o = outfit(),
      vest = new THREE.Color(0xf07a1a);
    const { b } = seat(
      P,
      state.jetSki,
      P.k * 1.15,
      V(0, 0.62, -0.12),
      'ride',
      (sg) => V(sg * 0.3, 0.78, 0.23),
      (name, mesh) => (/_Body_/.test(mesh) && name !== 'Skin' ? vest : o(name, mesh)),
    );
    const head = b('Head'),
      R = 0.1 / head.getWorldScale(new THREE.Vector3()).x,
      helmet = new THREE.Mesh(helmetGeo, Ph(0xf2f0ea, { roughness: 0.25 }));
    helmet.scale.setScalar(R);
    helmet.position.set(0, R * 0.62, -R * 0.06);
    head.add(helmet);
  }
  standSpots.forEach((sp, i) => {
    // paddlers wear swimwear; everyone else standing (bus stops) is in street clothes
    const fem = rnd() < 0.5,
      P = sp.sup ? prepared[fem ? 5 : 0] : crowd[(i * 2 + 3) % crowd.length],
      model = dress(SkeletonUtils.clone(P.root), P, sp.sup ? swimOutfit(fem) : outfit());
    const [wd, ht, belly] = build();
    fatten(model, belly);
    model.scale.set(P.k * wd, P.k * ht, P.k * wd);
    model.position.set(sp.x, sp.y + P.y0 * P.k * ht, sp.z);
    model.rotation.y = sp.ry;
    sp.parent.add(model);
    model.traverse((o) => {
      if (o.isSkinnedMesh) o.castShadow = true;
    });
    if (sp.sup) {
      P.paddle ??= makeClip('Paddle', P.root, P.idle, 1.6, paddleFrame);
      sp.action = new THREE.AnimationMixer(model).clipAction(P.paddle);
      sp.action.play();
      idlers.push(sp.action.getMixer());
      model.scale.setScalar(P.k * ht);
      model.position.y = sp.y + P.y0 * P.k * ht; // uniform scale: the arm IK assumes no shear
      const bn = (n) => model.getObjectByName(THREE.PropertyBinding.sanitizeNodeName(n));
      sp.arms = {
        L: ['UpperArm.L', 'LowerArm.L', 'Hand.L'].map(bn),
        R: ['UpperArm.R', 'LowerArm.R', 'Hand.R'].map(bn),
      };
      // the IK starts every frame from this pose (arms hanging), so nothing accumulates frame to frame
      sp.armRest = [...sp.arms.L, ...sp.arms.R].map((b) => [b, b.quaternion.clone()]);
      return;
    }
    const mixer = new THREE.AnimationMixer(model),
      a = mixer.clipAction(P.idle);
    a.play();
    a.time = rnd() * a.getClip().duration;
    idlers.push(mixer);
    Object.assign(sp, { model, mixer }); // Polyfork people may take the spot later
  });
}
